import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { GlobalService } from 'app/global/global.service';
import { PermissionsEnum } from 'app/global/permissions/permissions.enum';
import { PermissionsService } from 'app/global/permissions/permissions.service';
import { catchError, filter, map, of, switchMap, take, tap } from 'rxjs';
import { DocumentTypeCustomBoxesData, ICustomBoxesDaum } from '../../contract-control/contract-control.dto';
import { DocumentFieldService } from '../../settings/documentfields/documentfield.service';
import { MetaDataFormComponent } from '../../settings/documentfields/meta-data-form/meta-data-form.component';
import { IMetadataItemDto } from '../../settings/documentfields/metadata-item.dto';
import { DocumentFieldsFacade } from '../../settings/documentfields/store/documentfields.facade';
import { IField } from '../../settings/documenttype/document-type-settings-modal/document-type-settings.dto';


export enum ViewEnum {
  CONTRACT_CONTROL = 'contract_control',
  DOCUMENT_FIELD = 'document_field',
}

export interface ISearcableMetaData extends Partial<IMetadataItemDto> {}


@Component({
  selector: 'app-search-meta-data-modal',
  templateUrl: './search-meta-data-modal.component.html',
  styleUrl: './search-meta-data-modal.component.scss'
})


export class SearchMetaDataModalComponent implements OnInit {

  @Input() metaDataFieldModalRef: NgbModalRef;
  @Input() document_type_custom_boxe_id: number = 0;
  @Input() customBoxes: ICustomBoxesDaum[] = [];
  @Input() section: string = ViewEnum.DOCUMENT_FIELD;
  @Input() view: ViewEnum = ViewEnum.CONTRACT_CONTROL;
  @Input() documentTypeId:number;

  @ViewChild('fieldautocomplete', { static: false }) fieldautocomplete;


  public loading: boolean = false;
  public metaData: IMetadataItemDto[] = [];
  public metadata_keyword: string = 'meta_data_name';
  public canAccessDocumentSettings$: boolean = false;
  public selectedField: ISearcableMetaData;

  constructor(
    private _documentFieldService$: DocumentFieldService,
    private modalService: NgbModal,
    private _globalService$: GlobalService,
    private _permissionService$: PermissionsService,
    private _translocoService: TranslocoService,
    private _activeModalRef: NgbActiveModal,
    private documentFieldsFacade: DocumentFieldsFacade,
  ) { this.customMetaDataFilter = this.customMetaDataFilter.bind(this); }


  /**
   * Angular lifecycle hook that is called after the component's data-bound properties have been initialized.
   * 
   * This method retrieves the user's default role and checks if the user has permission to access document settings.
   * The result is assigned to `canAccessDocumentSettings$`.
   * 
   * @remarks
   * - Uses RxJS operators to handle asynchronous permission checks.
   * - Only the first emitted value from each observable is used.
   */
  ngOnInit(): void {
    this._permissionService$.getUserDefaultRole().pipe(
      filter(role => !!role),
      take(1),
    ).subscribe(res => {
      this._permissionService$.hasPermissionInRole(res?.name, PermissionsEnum.settings_meta_data).pipe(
        take(1)
      ).subscribe((hasPermission: boolean) => this.canAccessDocumentSettings$ = hasPermission);
    });

    this.documentFieldsFacade.documentFields$
    .pipe(
      take(1),
      switchMap(metaData => {
        if (metaData.length > 0) {
          return of(metaData);
        }
        this.documentFieldsFacade.loadDocumentFields(1);
        return this.documentFieldsFacade.documentFields$.pipe(
          filter(loadedMetaData => loadedMetaData.length > 0),
          take(1)
        );
      })
    )
    .subscribe({
      next: (metaData: IMetadataItemDto[]) => {
        this.metaData = [...metaData];
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      }
    });
  }




  
  /**
   * Handles the selection of a metadata item from the search modal.
   * 
   * - If the selected item is a new metadata field (`item.id === 0`), opens a modal to create or edit the metadata field.
   *   - Passes relevant data to the modal component instance.
   *   - On modal close, updates the `selectedField` with the new metadata item.
   * - If the selected item already exists:
   *   - Checks if the field exists in another custom box and shows a warning if so.
   *   - Otherwise, prepares the form data, updates `selectedField`, and, if in CONTRACT_CONTROL view, updates the document field via a service call.
   * - Closes the autocomplete dropdown if open.
   * - Clears the `metaData` array after selection.
   * 
   * @param item The metadata item selected by the user.
   */
 selectMetaData(item: IMetadataItemDto): void {
      this.getMetadataSelection(item)
          .then((selectedField)=>{
              this.selectedField = selectedField;

              if (this.view === ViewEnum.CONTRACT_CONTROL && selectedField != null) {
                  this._documentFieldService$.assignMetadataToDocument(this.documentTypeId, this.document_type_custom_boxe_id, this.selectedField?.id)
                      .pipe(take(1))
                      .subscribe({
                          next:response => {
                              this.selectedField = { ...this.selectedField,  document_type_custom_boxes_data: response};
                          },
                          error: error =>this._globalService$.toastrError(error)
                      });
              }

              if (this.fieldautocomplete) {
                  this.fieldautocomplete.close();
              }
          })
 }

  /**
   * Handles the insertion of a selected field from the modal.
   *
   * If a field is selected (`this.selectedField`), the method closes the active modal
   * and returns the selected field. If no field is selected, it displays a warning
   * message prompting the user to select a field before proceeding.
   */
  insertField(): void {
    if (this.selectedField) {
      this._activeModalRef.close(this.selectedField);
    } else {
      this._globalService$.toastrWarning(this._translocoService.translate('Please select a field to insert'));
    }
  }


  
  /**
   * Clears the custom field values by resetting the `metaData` array to empty
   * and setting the `selectedField` to null.
   *
   * @remarks
   * This method is typically used to reset the state of the custom field selection
   * in the search meta data modal component.
   */
  clearCustomfieldValue(): void {
    this.selectedField = null;
  }

 
  /**
   * Filters a list of metadata items based on a query string and appends a custom suggestion item.
   *
   * @param items - An array of metadata objects, each containing `id`, `meta_data_name`, and `query` properties.
   * @param query - The search string used to filter the metadata items.
   * @returns An array of filtered metadata items whose `meta_data_name` includes the query string (case-insensitive),
   *          followed by a custom suggestion item constructed using the query and a translated string.
   */
  customMetaDataFilter = (items: { id: number, meta_data_name: string, query: string }[], query: string) => {
    const results: { id: number, meta_data_name: string, query: string }[] = [];
    items.forEach(item => {
      if ((item['meta_data_name']).toLowerCase().indexOf(query.toLowerCase()) > -1) {
        results.push(item);
      }
    });
    if(results.length === 0) {
      const searchQuery = this._translocoService.translate('Add field name as document field suggestion text from (Contract control section - Search meta data) - Add Field name as Document field', { fieldName: query === '' ? this._translocoService.translate('Search meta data modal adding new field text (Search meta data modal) - New field') : query })
        results.push({ id: 0, meta_data_name: query, query: searchQuery });
    }
    return results;
  }



  closeModal(): void {
    this._activeModalRef.close();
  }

  private async addNewMetadataModal(item: IMetadataItemDto):Promise<ISearcableMetaData | null>{
      const documentFieldModalRef: NgbModalRef = this.modalService.open(MetaDataFormComponent);
      documentFieldModalRef.componentInstance.meta_data_name = item?.meta_data_name;
      if(this.view === ViewEnum.CONTRACT_CONTROL) {
        documentFieldModalRef.componentInstance.document_type_custom_boxe_id = this.document_type_custom_boxe_id;
      }
      documentFieldModalRef.componentInstance.section = ViewEnum.DOCUMENT_FIELD;
      documentFieldModalRef.componentInstance.view = 'modal';
      documentFieldModalRef.componentInstance.custom_box_field = this.view === ViewEnum.CONTRACT_CONTROL;
      const documentfield: IMetadataItemDto = await documentFieldModalRef.result;
      if (documentfield) {
       this.documentFieldsFacade.loadDocumentFields(1);
       return documentfield;
     }
  }

 private selectedMetadata(item: IMetadataItemDto):ISearcableMetaData | null {
     const fieldExistsInOtherBox: ICustomBoxesDaum = this.customBoxes?.find(customBox => {
        const boxesData = customBox?.document_type_custom_boxes_data || [];
        return boxesData.some(i => i.meta_data_id === item.id);
      });
      if (fieldExistsInOtherBox) {
        this._globalService$.toastrWarning(this._translocoService.translate('Field already exists toastr message (Edit layout popup) - Field already exists in custom box', {
          fieldName: item.meta_data_name,
          boxName: fieldExistsInOtherBox?.name
        }));

        return;
      }

      return item;
 }
 
 private async getMetadataSelection(item: IMetadataItemDto): Promise<ISearcableMetaData | null> {
     if (item.id === 0) {
        return await this.addNewMetadataModal(item);
     }

     return this.selectedMetadata(item);
 }
}
